import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usersService } from "../../chat/api/users";
import type { CursorPage, User } from "../../chat/api/types";

/**
 * Organisation-directory search for the in-call "ring colleagues" picker (#1384).
 *
 * The directory lives on **chat**, not video (D15): `GET /api/v1.0/users/?search=`
 * is org-scoped by chat's own tenant mixin, and the `sub` it returns *is* the
 * `callee_sub` the ring endpoint expects — no mapping in between. Video has no
 * directory of its own to reuse (its `User` model has no organization field at
 * all), so borrowing chat's is not a shortcut, it is the only source.
 *
 * Reaching across module boundaries for a service is established practice here
 * — `useTodaysVideoEvents` imports planner's `calendarService` the same way.
 * We import the *service*, not chat's `useUserSearch`, so this module owns its
 * own cache key and cannot invalidate chat's.
 *
 * Accepted cost of D15: the picker is only as available as the chat service.
 * The server makes the same bet and fails closed (503) if chat is unreachable,
 * so a chat outage blocks ringing outright rather than ringing the wrong people.
 */

const SEARCH_DEBOUNCE_MS = 300;

/** Below this the query does not fire at all — a one-character search would
 *  return an arbitrary slice of the whole organisation. */
const MIN_QUERY_LENGTH = 2;

const RESULT_LIMIT = 10;

export const orgDirectorySearchKey = (q: string) =>
  ["call", "directory", "search", q] as const;

export function useOrgDirectorySearch(q: string) {
  const [debouncedQ, setDebouncedQ] = useState(q);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  return useQuery<CursorPage<User>>({
    queryKey: orgDirectorySearchKey(debouncedQ),
    queryFn: () => usersService.list({ search: debouncedQ, limit: RESULT_LIMIT }),
    enabled: debouncedQ.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
  });
}

export { MIN_QUERY_LENGTH as ORG_DIRECTORY_MIN_QUERY_LENGTH };
